/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { AgendaFretesPage } from './pages/AgendaFretes';
import { Pessoas } from './pages/Pessoas';
import { Veiculos } from './pages/Veiculos';
import { OrdensServico } from './pages/OrdensServico';
import { MedicoesPage } from './pages/Medicoes';
import { FinanceiroPage } from './pages/Financeiro';
import { Propostas } from './pages/Propostas';
import { OperacionalPage } from './pages/Operacional';
import { SistemaPage } from './pages/Sistema';
import { FiscalPage } from './pages/FiscalPage';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { FirebaseAuthService } from './services/firebaseAuthService';
import { registrarPushNativo } from './services/pushNativoService';
import { User as FirebaseUser } from 'firebase/auth';
import { supabase } from './lib/supabase';

import { PermissionsDemo } from './pages/PermissionsDemo';
import { usePermissionsContext } from './contexts/PermissionsContext';
import { RoleProtectedRoute } from './components/ProtectedRoute';

import { AreaClienteProvider } from './contexts/AreaClienteContext';
import { AreaClienteLayout } from './pages/AreaCliente/AreaClienteLayout';
import { AreaClienteDashboard } from './pages/AreaCliente/AreaClienteDashboard';
import { AreaClienteOrdensServico } from './pages/AreaCliente/AreaClienteOrdensServico';
import { AreaClienteMedicoes } from './pages/AreaCliente/AreaClienteMedicoes';
import { AreaClienteFinanceiro } from './pages/AreaCliente/AreaClienteFinanceiro';
import { AreaClienteSolicitarFrete } from './pages/AreaCliente/AreaClienteSolicitarFrete';
import { AreaClientePropostas } from './pages/AreaCliente/AreaClientePropostas';

import { AreaMotoristaProvider } from './contexts/AreaMotoristaContext';
import { AreaMotoristaLayout } from './pages/AreaMotorista/AreaMotoristaLayout';
import { AreaMotoristaDashboard } from './pages/AreaMotorista/AreaMotoristaDashboard';
import { AreaMotoristaMinhasViagens } from './pages/AreaMotorista/AreaMotoristaMinhasViagens';
import { AreaMotoristaExecucaoOS } from './pages/AreaMotorista/AreaMotoristaExecucaoOS';
import { AreaMotoristaExtrato } from './pages/AreaMotorista/AreaMotoristaExtrato';
import { AreaMotoristaChecklist } from './pages/AreaMotorista/AreaMotoristaChecklist';
import { AreaMotoristaPerfil } from './pages/AreaMotorista/AreaMotoristaPerfil';
import { AreaMotoristaRoteiroDia } from './pages/AreaMotorista/AreaMotoristaRoteiroDia';

function ProtectedRoute({ children, user }: { children: React.ReactNode; user: FirebaseUser | null }) {
  const location = useLocation();
  const { role, isLoading } = usePermissionsContext();

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Se o usuário logado for motorista / agregado, redireciona para a área do motorista
  if (role === 'driver') {
    return <Navigate to="/area-motorista" replace />;
  }

  // Se o usuário logado for cliente, redireciona para a área do cliente
  if (role === 'client') {
    return <Navigate to="/area-cliente" replace />;
  }

  if (role === 'guest' && !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// Guarda simples para o Portal do Cliente que preserva o destino original
// (ex: /area-cliente/os?os=123, vindo de um link de notificação de WhatsApp)
// para retomar depois do login, em vez de sempre cair no dashboard.
function AreaClienteGuard({ user, children }: { user: FirebaseUser | null; children: React.ReactNode }) {
  const location = useLocation();
  if (!user) {
    const destino = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?redirect=${destino}`} replace />;
  }
  return <>{children}</>;
}

export default function App() {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Escuta estado de autenticação em tempo real no Firebase Auth
    const unsubscribe = FirebaseAuthService.onAuthStateChange((user) => {
      setCurrentUser(user);
      setIsLoading(false);
      // Só tem efeito dentro do app Android empacotado (Capacitor); em um
      // navegador comum, registrarPushNativo() não faz nada.
      if (user?.email) {
        registrarPushNativo(user.email);
      }
    });

    return () => unsubscribe();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={currentUser ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/forgot-password" element={currentUser ? <Navigate to="/" replace /> : <ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* Portal Área do Cliente */}
        <Route
          path="/area-cliente"
          element={
            <AreaClienteGuard user={currentUser}>
              <AreaClienteProvider>
                <AreaClienteLayout />
              </AreaClienteProvider>
            </AreaClienteGuard>
          }
        >
          <Route index element={<AreaClienteDashboard />} />
          <Route path="os" element={<AreaClienteOrdensServico />} />
          <Route path="medicoes" element={<AreaClienteMedicoes />} />
          <Route path="financeiro" element={<AreaClienteFinanceiro />} />
          <Route path="solicitar-frete" element={<AreaClienteSolicitarFrete />} />
          <Route path="propostas" element={<AreaClientePropostas />} />
        </Route>

        {/* Portal Área do Motorista */}
        <Route
          path="/area-motorista"
          element={
            currentUser ? (
              <AreaMotoristaProvider>
                <AreaMotoristaLayout />
              </AreaMotoristaProvider>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          <Route index element={<AreaMotoristaDashboard />} />
          <Route path="roteiro" element={<AreaMotoristaRoteiroDia />} />
          <Route path="viagens" element={<AreaMotoristaMinhasViagens />} />
          <Route path="execucao" element={<AreaMotoristaExecucaoOS />} />
          <Route path="extrato" element={<AreaMotoristaExtrato />} />
          <Route path="checklist" element={<AreaMotoristaChecklist />} />
          <Route path="perfil" element={<AreaMotoristaPerfil />} />
        </Route>

        <Route path="/" element={<ProtectedRoute user={currentUser}><Layout /></ProtectedRoute>}>
          <Route index element={<Dashboard />} />
          <Route path="agenda" element={<RoleProtectedRoute requiredPermission="view_orders"><AgendaFretesPage /></RoleProtectedRoute>} />
          <Route path="operacional" element={<RoleProtectedRoute requiredPermission="view_routes"><OperacionalPage /></RoleProtectedRoute>} />
          <Route path="pessoas" element={<RoleProtectedRoute requiredPermission="view_people"><Pessoas /></RoleProtectedRoute>} />
          <Route path="veiculos" element={<RoleProtectedRoute requiredPermission="view_vehicles"><Veiculos /></RoleProtectedRoute>} />
          <Route path="os" element={<RoleProtectedRoute requiredPermission="view_orders"><OrdensServico /></RoleProtectedRoute>} />
          <Route path="medicoes" element={<RoleProtectedRoute requiredPermission="view_medicoes"><MedicoesPage /></RoleProtectedRoute>} />
          <Route path="financeiro" element={<RoleProtectedRoute requiredPermission="view_financial"><FinanceiroPage /></RoleProtectedRoute>} />
          <Route path="fiscal" element={<RoleProtectedRoute requiredPermission="view_fiscal"><FiscalPage /></RoleProtectedRoute>} />
          <Route path="propostas" element={<RoleProtectedRoute requiredPermission="view_proposals"><Propostas /></RoleProtectedRoute>} />
          <Route path="sistema" element={<RoleProtectedRoute anyOfPermissions={['manage_users', 'manage_system_settings', 'manage_groups', 'manage_permissions', 'view_audit_logs']}><SistemaPage /></RoleProtectedRoute>} />
          <Route path="config" element={<Navigate to="/sistema" replace />} />
          <Route path="permissions" element={<RoleProtectedRoute requiredRole="admin"><PermissionsDemo /></RoleProtectedRoute>} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
